import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, login } = useUser();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: '',
    coachName: '',
    coachId: ''
  });

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      if (user.role === 'master_admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (user.role === 'coach') {
        navigate('/coach/dashboard', { replace: true });
      } else {
        navigate('/client/hub', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    
    // Use XMLHttpRequest to avoid any fetch interceptors
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/auth/login`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.withCredentials = true;
    
    xhr.onload = function() {
      setLoading(false);
      try {
        const data = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 400) {
          setError(data.detail || 'Login failed');
          return;
        }
        
        if (!data.user) {
          setError('Invalid response from server');
          return;
        }
        
        login(data.user, data.access_token);
        
        if (data.user.role === 'master_admin') {
          navigate('/admin/dashboard');
        } else if (data.user.role === 'coach') {
          navigate('/coach/dashboard');
        } else {
          navigate('/client/hub');
        }
      } catch (parseError) {
        setError('Login failed. Please try again.');
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      setError('Network error. Please try again.');
    };
    
    xhr.send(JSON.stringify({
      email: formData.email,
      password: formData.password
    }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: 'client',
          coach_name: formData.coachName || undefined,
          coach_id: formData.coachId || undefined
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      login(data.user, data.access_token);
      navigate('/client/hub');
    } catch (err) {
      setError(err.message || 'Failed to register');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory-mist flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo & Tagline */}
        <div className="text-center mb-8">
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight text-obsidian-night mb-2">
            <span className="lowercase">gntl</span> <span className="uppercase">STRNGTH</span>
          </h1>
          <p className="text-shadow-pine/70 font-body text-sm">
            Welcome to the Architecture of You
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-white/80 backdrop-blur-sm border border-burnt-terra/20 rounded-2xl shadow-xl p-8">
          {/* Toggle Tabs */}
          <div className="flex mb-6 bg-ivory-mist rounded-lg p-1">
            <button
              onClick={() => { setIsLogin(true); setError(''); }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                isLogin 
                  ? 'bg-white text-obsidian-night shadow-sm' 
                  : 'text-shadow-pine hover:text-obsidian-night'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => { setIsLogin(false); setError(''); }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                !isLogin 
                  ? 'bg-white text-obsidian-night shadow-sm' 
                  : 'text-shadow-pine hover:text-obsidian-night'
              }`}
            >
              Register
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Login Form */}
          {isLogin ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-shadow-pine">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="border-shadow-pine/30 focus:border-burnt-terra focus:ring-burnt-terra"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-shadow-pine">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="border-shadow-pine/30 focus:border-burnt-terra focus:ring-burnt-terra"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-burnt-terra hover:bg-burnt-terra/90 text-white font-medium py-5"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          ) : (
            /* Signup Form */
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-shadow-pine">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="border-shadow-pine/30 focus:border-burnt-terra focus:ring-burnt-terra"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-email" className="text-shadow-pine">Email</Label>
                <Input
                  id="signup-email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="border-shadow-pine/30 focus:border-burnt-terra focus:ring-burnt-terra"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signup-password" className="text-shadow-pine">Password</Label>
                <Input
                  id="signup-password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="border-shadow-pine/30 focus:border-burnt-terra focus:ring-burnt-terra"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-shadow-pine">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="border-shadow-pine/30 focus:border-burnt-terra focus:ring-burnt-terra"
                />
              </div>

              {/* Optional Coach Info */}
              <div className="pt-2 border-t border-shadow-pine/10">
                <p className="text-xs text-shadow-pine/60 mb-3">Optional: Connect with your coach</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="coachName" className="text-shadow-pine text-xs">Coach Name</Label>
                    <Input
                      id="coachName"
                      name="coachName"
                      type="text"
                      placeholder="Coach name"
                      value={formData.coachName}
                      onChange={handleInputChange}
                      className="border-shadow-pine/30 focus:border-burnt-terra focus:ring-burnt-terra text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="coachId" className="text-shadow-pine text-xs">Coach ID</Label>
                    <Input
                      id="coachId"
                      name="coachId"
                      type="text"
                      placeholder="e.g., 0001"
                      value={formData.coachId}
                      onChange={handleInputChange}
                      className="border-shadow-pine/30 focus:border-burnt-terra focus:ring-burnt-terra text-sm"
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-burnt-terra hover:bg-burnt-terra/90 text-white font-medium py-5"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
          )}
        </div>

        {/* Footer Quote */}
        <p className="text-center text-shadow-pine/50 text-xs mt-6 font-body">
          &ldquo;Train coordinates, not muscles.&rdquo;
        </p>
      </div>
    </div>
  );
};

export default LandingPage;
